import React, { useEffect, useRef } from 'react'

import { ActionButton, RfShell } from '../components/RfShell'
import { RfStatusBanner } from '../components/RfStatusBanner'
import { receivingContext, ReceivingFlowController } from '../flow'
import { fieldLabels, measurementUnit, packLabels, storedMeasurement } from '../measurements'
import {
  DecisionActions,
  DecisionButton,
  DecisionDialog,
  DialogBackdrop,
  PhotoEvidenceField,
  PhotoEvidenceHeading,
  PhotoActions,
  ScanForm,
  TaskHeader,
} from '../styles'

export const PackMeasurementScreen = ({
  controller,
  embedded = false,
  onExit,
}: {
  controller: ReceivingFlowController
  embedded?: boolean
  onExit: () => void
}) => {
  const { state, dispatch } = controller
  const m = state.measurements
  const input = useRef<HTMLInputElement>(null)
  const photoAction = useRef<HTMLButtonElement>(null)
  const replacePhotos = useRef(false)
  const cancel = useRef<HTMLButtonElement>(null)
  const dialog = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (m?.dialog) cancel.current?.focus()
    else if (m?.photo) photoAction.current?.focus()
    else input.current?.focus()
  }, [m?.level, m?.field, m?.photo, m?.dialog])
  if (!m) return null
  const pack = m.packs[m.level]
  const stored = storedMeasurement(m, state.fixture.preprocessProfiles?.[state.activeVariant?.id ?? ''] ?? null)
  const unit = measurementUnit(m)
  const title = m.photo
    ? 'Capture photo'
    : `Enter ${['ti', 'hi'].includes(m.field) ? fieldLabels[m.field] : fieldLabels[m.field].toLowerCase()}`
  const overlay = m.dialog ? (
    <DialogBackdrop>
      <DecisionDialog
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="measurement-dialog-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            dispatch({ type: 'cancel-measurement-dialog' })
          }
          if (event.key === 'Tab') {
            const buttons = dialog.current?.querySelectorAll('button')
            if (!buttons?.length) return
            if (event.shiftKey && document.activeElement === buttons[0]) {
              event.preventDefault()
              buttons[buttons.length - 1].focus()
            } else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) {
              event.preventDefault()
              buttons[0].focus()
            }
          }
        }}
      >
        <h2 id="measurement-dialog-title">
          {m.dialog === 'difference' ? 'Update measurement?' : `Confirm no ${packLabels[m.level].toLowerCase()}?`}
        </h2>
        <p>{packLabels[m.level]}</p>
        {m.dialog === 'difference' ? (
          <>
            <p>
              Recorded: {stored} {unit}
            </p>
            <p>
              Entered: {m.input} {unit}
            </p>
          </>
        ) : (
          <p>{pack.photos.length} photo(s) attached. Continue without this pack level?</p>
        )}
        <DecisionActions>
          <DecisionButton ref={cancel} type="button" onClick={() => dispatch({ type: 'cancel-measurement-dialog' })}>
            Cancel
          </DecisionButton>
          <DecisionButton
            $primary
            type="button"
            onClick={() =>
              dispatch({ type: m.dialog === 'difference' ? 'confirm-measurement-update' : 'confirm-pack-absence' })
            }
          >
            {m.dialog === 'difference' ? 'Confirm update' : 'Confirm'}
          </DecisionButton>
        </DecisionActions>
      </DecisionDialog>
    </DialogBackdrop>
  ) : null
  return (
    <RfShell
      title="Receiving"
      phase="Preprocessing"
      embedded={embedded}
      contextSummary={receivingContext(state)}
      onBack={() => dispatch({ type: 'back' })}
      moreActions={[{ label: 'Exit receiving', onSelect: onExit, danger: true }]}
      overlay={overlay}
      actions={
        <>
          {m.photo ? (
            <ActionButton
              type="button"
              disabled={pack.photos.length === 0}
              onClick={() => dispatch({ type: 'accept-measurement-photos' })}
            >
              Accept photos
            </ActionButton>
          ) : (
            <ActionButton type="submit" form="pack-measurement-form">
              Continue
            </ActionButton>
          )}
          {!m.photo && (m.level === 'inner' || m.level === 'carton') && m.field === 'quantity' ? (
            <ActionButton $secondary type="button" onClick={() => dispatch({ type: 'skip-pack' })}>
              No {packLabels[m.level].toLowerCase()}
            </ActionButton>
          ) : null}
        </>
      }
    >
      <TaskHeader>
        <h1>{title}</h1>
        <p>
          {packLabels[m.level]}
          {pack.absent ? ' — not present' : ''}
        </p>
      </TaskHeader>
      {m.error ? <RfStatusBanner tone="error">{m.error}</RfStatusBanner> : null}
      {m.photo ? (
        <PhotoEvidenceField>
          <PhotoEvidenceHeading>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M8 5l2-2h4l2 2h4a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V7a2 2 0 012-2z" />
              <circle cx="12" cy="12" r="4" />
            </svg>
            <h2>
              {pack.photos.length
                ? `${pack.photos.length} photo${pack.photos.length === 1 ? '' : 's'} attached`
                : 'No photos yet'}
            </h2>
          </PhotoEvidenceHeading>
          <p>
            {pack.photos.length
              ? 'Review your photos before continuing.'
              : 'Capture the product and its packaging clearly.'}
          </p>
          <input
            ref={input}
            id="measurement-photo"
            aria-label="Photo evidence"
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(event) => {
              const files = Array.from(event.currentTarget.files ?? []).filter((file) => file.type.startsWith('image/'))
              if (files.length) {
                if (replacePhotos.current) dispatch({ type: 'retake-measurement-photos' })
                dispatch({ type: 'add-measurement-photos', names: files.map((file) => file.name) })
              }
              replacePhotos.current = false
              event.currentTarget.value = ''
            }}
          />
          {pack.photos.length > 0 ? (
            <ul aria-label="Attached photos">
              {pack.photos.map((name, index) => (
                <li key={`${index}-${name}`}>{name}</li>
              ))}
            </ul>
          ) : null}
          {pack.photos.length ? (
            <PhotoActions>
              <ActionButton
                $secondary
                ref={photoAction}
                type="button"
                onClick={() => {
                  replacePhotos.current = true
                  input.current?.click()
                }}
              >
                Retake
              </ActionButton>
              <ActionButton
                $secondary
                type="button"
                onClick={() => {
                  replacePhotos.current = false
                  input.current?.click()
                }}
              >
                Add photo
              </ActionButton>
            </PhotoActions>
          ) : (
            <ActionButton
              ref={photoAction}
              type="button"
              onClick={() => {
                replacePhotos.current = false
                input.current?.click()
              }}
            >
              Take photo
            </ActionButton>
          )}
        </PhotoEvidenceField>
      ) : (
        <ScanForm
          id="pack-measurement-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            dispatch({ type: 'submit-measurement' })
          }}
        >
          <p>Recorded: {stored === null ? 'Not recorded' : `${stored} ${unit}`}</p>
          <label htmlFor="measured-value">
            {fieldLabels[m.field]} ({unit})
          </label>
          <input
            ref={input}
            id="measured-value"
            className="form-control"
            type="text"
            inputMode={['quantity', 'ti', 'hi'].includes(m.field) ? 'numeric' : 'decimal'}
            autoComplete="off"
            value={m.input}
            aria-invalid={!!m.error}
            onChange={(event) => dispatch({ type: 'set-measurement', value: event.target.value })}
          />
        </ScanForm>
      )}
    </RfShell>
  )
}
